import React, { useContext, useState } from 'react';

// Enable accordingly
import { HttpMethods, InsightContext, FetchContext, ApiRoutes } from '../../contexts';
import { useAggregatedState, RequestStatus } from '../../../helpers';

export const CustomerRequestsContext = React.createContext(null);

export const CustomerRequestsProvider = (props) => {
    const { fetchAuthorized } = useContext(FetchContext);
    const { trackError } = useContext(InsightContext);

    const [ state, setState ] = useAggregatedState({     
        requests: [],
        isSearchingMode: false,
        totalItems: 0,
        involved: []
    });

    const [isLoadingRequests, setIsLoadingRequests] = useState(false);
    const [isSearching, setIsSearching] = useState(false);
    const loadRequests = async (filter, searchText, pageIndex, pageSize) => {
        try {
            if(searchText !== null && searchText !== undefined)
                setIsSearching(true);
            else
                setIsLoadingRequests(true);
            
            let apiUrl = null;
            searchText = searchText && searchText.length > 0 ? searchText : null;

            switch(filter) {            
                case "draft":
                    apiUrl = ApiRoutes.Request.GetDraftRequests({ pageIndex, pageSize, searchText });
                    break;
                case "running":
                    apiUrl = ApiRoutes.Request.GetRunningRequests({ pageIndex, pageSize, searchText });
                    break;
                case "completed":
                    apiUrl = ApiRoutes.Request.GetCompletedRequests({ pageIndex, pageSize, searchText });
                    break;
                default:
                    return;
            }

            const response = await fetchAuthorized(
                apiUrl,
                { method: HttpMethods.GET }
            );

            const object = await response.json();
            const totalItems = object.totalItems;
            const requests = object.items;
            const involved = requests.map(x => ({ customerRequestId: x.customerRequestId, isLoading: false, users: null }));
            setState({ requests, involved, totalItems });
        }
        catch(e) {
            console.error(e);
            trackError(e);
        }
        finally {
            setIsSearching(false);
            setIsLoadingRequests(false);
        }
    }

    const [isDeletingRequest, setIsDeletingRequest] = useState(false);
    const deleteDraftRequest = async (customerRequestId, dbType) => {
        try {
            setIsDeletingRequest(true);

            const _ = await fetchAuthorized(
                ApiRoutes.Request.DeleteCustomerRequestDraft({ customerRequestId, dbType }),
                { method: HttpMethods.DELETE }
            );

            const requests = [].concat(state.requests.filter(x => x.customerRequestId !== customerRequestId));
            setState({requests});
            setState({totalItems: state.totalItems - 1});

            return true;
        }
        catch(e) {
            console.error(e);
            trackError(e);

            return false;
        }
        finally {
            setIsDeletingRequest(false);
        }
    };

    const [isAbortingRequest, setIsAbortingRequest] = useState(false);
    const abortCustomerRequest = async (customerRequestId, dbType) => {
        try {
            setIsAbortingRequest(true);

            const _ = await fetchAuthorized(
                ApiRoutes.Request.AbortCustomerRequest({ customerRequestId, dbType }),
                { method: HttpMethods.DELETE }
            );

            const requests = [].concat(state.requests.filter(x => x.customerRequestId !== customerRequestId));
            setState({ requests });
            setState({ totalItems: state.totalItems - 1 });
        }
        catch(e) {
            console.error(e);
            trackError(e);
        }
        finally {
            setIsAbortingRequest(false);
        }
    };

    const isLoadingInvolvedUsers = (customerRequestId) => {        
        const item = state.involved.find(x => x.customerRequestId === customerRequestId);
        return item && item.isLoading;
    }

    const loadInvolvedUsers = async (customerRequestId, customerRequestStatus, dbType) => {
        const setIsLoadingInvolvedUsers = (customerRequestId, isLoading) => {
            const involved = [].concat(state.involved);
            const item = involved.find(x => x.customerRequestId === customerRequestId);
            item.isLoading = isLoading;
            setState({ involved });
        };

        try {
            setIsLoadingInvolvedUsers(customerRequestId, true);

            const response = await fetchAuthorized(
                ApiRoutes.User.GetInvolvedUsers({
                    customerRequestId,
                    onlyMissingUsers: customerRequestStatus === RequestStatus.Logistic,
                    dbType
                }),
                { method: HttpMethods.GET }
            );
            
            const users = await response.json();                    
            const involved = [].concat(state.involved).filter(x => x.customerRequestId !== customerRequestId);
            const item = { customerRequestId, isLoading: false, users };
            involved.push(item);
            setState({ involved });            
        }
        catch(e) {
            console.error(e);
            trackError(e);       
            setIsLoadingInvolvedUsers(customerRequestId, false);
        }
    };

    const hasLodedInvolvedUsers = (customerRequestId) => {
        const item = state.involved.find(x => x.customerRequestId === customerRequestId);
        return item !== undefined && item !== null && (item.isLoading || item.users !== null);
    }

    const getInvolvedUsers = (customerRequestId) => {
        const item = state.involved.find(x => x.customerRequestId === customerRequestId);

        if(!item)
            return [];

        return item.users;
    };

    const { Provider } = CustomerRequestsContext;

    return (
        <Provider value={{
            state,
            isLoadingRequests,
            isSearching,
            loadRequests,

            isDeletingRequest,
            deleteDraftRequest,

            isAbortingRequest,
            abortCustomerRequest,

            loadInvolvedUsers,
            hasLodedInvolvedUsers,
            isLoadingInvolvedUsers,
            getInvolvedUsers            
        }}>
            {props.children}
        </Provider>
    );
}